import { useState, useMemo, useEffect } from "react";
import { ExternalLink, ChevronDown, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, Eye } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { pl } from "date-fns/locale";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { QUALITY_FLAGS_LABELS } from "@/constants/wpQualityFlags";
import type { WpPostRow } from "@/hooks/useExistingPosts";
import { WpPostPreviewModal } from "./WpPostPreviewModal";

interface WpPostsTableProps {
  posts: WpPostRow[];
  loading?: boolean;
}

type SortKey = "word_count" | "quality_score" | "published_at";
type SortDir = "asc" | "desc";

function QualityBadge({
  score,
  recommendation,
}: {
  score: number | null;
  recommendation: string | null;
}) {
  if (score === null || score === undefined) {
    return <Badge variant="outline" className="text-[10px]">—</Badge>;
  }
  const color =
    recommendation === "keep"
      ? "bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30"
      : recommendation === "borderline"
        ? "bg-yellow-500/15 text-yellow-700 dark:text-yellow-400 border-yellow-500/30"
        : "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30";
  return <Badge className={`text-xs ${color} border`}>{score}/100</Badge>;
}

function RecommendationBadge({ rec }: { rec: string | null }) {
  if (!rec) return <span className="text-xs text-muted-foreground">—</span>;
  const cfg: Record<string, { label: string; color: string }> = {
    keep: { label: "Zachowaj", color: "bg-green-500/15 text-green-700 dark:text-green-400" },
    borderline: { label: "Borderline", color: "bg-yellow-500/15 text-yellow-700 dark:text-yellow-400" },
    refurbish: { label: "Przebudować", color: "bg-red-500/15 text-red-700 dark:text-red-400" },
  };
  const c = cfg[rec] || { label: rec, color: "bg-muted" };
  return <Badge className={`text-xs ${c.color}`}>{c.label}</Badge>;
}

const QUERY_FRAME_LABELS: Record<string, { label: string; color: string; desc: string }> = {
  definitional: { label: "Definicyjne", color: "bg-blue-500/15 text-blue-700 dark:text-blue-400", desc: "Co to jest X" },
  procedural: { label: "Proceduralne", color: "bg-violet-500/15 text-violet-700 dark:text-violet-400", desc: "Jak zrobić X" },
  comparative: { label: "Porównawcze", color: "bg-amber-500/15 text-amber-700 dark:text-amber-400", desc: "X vs Y" },
  causal: { label: "Przyczynowe", color: "bg-rose-500/15 text-rose-700 dark:text-rose-400", desc: "Dlaczego X" },
  evaluative: { label: "Ewaluacyjne", color: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400", desc: "Czy warto X" },
  mixed: { label: "Mieszane", color: "bg-slate-500/15 text-slate-700 dark:text-slate-400", desc: "Wiele intencji" },
};

/**
 * Status widocznosci SEO w Google. Zwiezly ale w pelnych slowach.
 */
function SeoBadge({
  lastSync,
  totalKeywords,
  top3,
  top10,
  top50,
  traffic,
  decay,
  cannib,
}: {
  lastSync: string | null;
  totalKeywords: number | null;
  top3: number | null;
  top10: number | null;
  top50?: number | null;
  traffic: number | null;
  decay: boolean | null;
  cannib: boolean | null;
}) {
  // Bez sync — szary
  if (!lastSync) {
    return (
      <div
        className="flex items-center gap-1.5 text-muted-foreground"
        title="Brak synchronizacji z Senuto — kliknij 'Sync SEO' w panelu wyżej"
      >
        <span className="w-2 h-2 rounded-full bg-muted-foreground/40" />
        <span className="text-[11px]">Brak danych</span>
      </div>
    );
  }

  const kw = totalKeywords ?? 0;
  const t3 = top3 ?? 0;
  const t10 = top10 ?? 0;
  const t50 = top50 ?? 0;
  const tr = traffic ?? 0;

  // Status widocznosci
  let statusLabel: string;
  let dotColor: string;
  let textColor: string;
  let subtitle: string;

  if (kw === 0 || t50 === 0) {
    statusLabel = "Niewidoczny";
    dotColor = "bg-zinc-500";
    textColor = "text-muted-foreground";
    subtitle = kw === 0 ? "0 fraz w Google" : `${kw} fraz poza TOP 50`;
  } else if (t3 >= 1 || tr >= 20) {
    statusLabel = "Dobrze widoczny";
    dotColor = "bg-emerald-500";
    textColor = "text-emerald-600 dark:text-emerald-400";
    subtitle = t3 >= 1 ? `${t3} fraz w TOP 3` : `${t10} w TOP 10`;
  } else if (t10 >= 1) {
    statusLabel = "Widoczny";
    dotColor = "bg-blue-500";
    textColor = "text-blue-600 dark:text-blue-400";
    subtitle = `${t10} fraz w TOP 10`;
  } else {
    statusLabel = "Nisko";
    dotColor = "bg-amber-500";
    textColor = "text-amber-600 dark:text-amber-400";
    subtitle = `${kw} fraz, pozycje 11–50`;
  }

  const tooltip = `${kw} fraz w Google · TOP 3: ${t3}, TOP 10: ${t10}, TOP 50: ${t50} · szacowany ruch ${tr}/mies${decay ? " · ⚠ CONTENT DECAY" : ""}${cannib ? " · ⚠ KANIBALIZACJA" : ""}`;

  return (
    <div className="flex flex-col gap-1 min-w-0" title={tooltip}>
      {/* Status — kropka + label */}
      <div className={`flex items-center gap-1.5 ${textColor}`}>
        <span className={`w-2 h-2 rounded-full ${dotColor} shrink-0`} />
        <span className="text-xs font-semibold leading-none">{statusLabel}</span>
      </div>

      {/* Podtytuł — 1 wiersz, pełnymi słowami */}
      <div className="text-[11px] text-muted-foreground leading-tight">
        {subtitle}
      </div>

      {/* Ruch — osobna linia gdy > 0 */}
      {tr > 0 && (
        <div className="text-[11px] leading-tight">
          <span className="text-muted-foreground">ruch: </span>
          <span className="font-semibold text-foreground">~{tr}</span>
          <span className="text-muted-foreground"> wizyt/mies</span>
        </div>
      )}

      {/* Alerty — tylko gdy wystepuja */}
      {(decay || cannib) && (
        <div className="flex gap-1 flex-wrap mt-0.5">
          {decay && (
            <span
              className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-600 dark:text-red-400 font-medium border border-red-500/20"
              title="Content decay — wpis utracił keywords w ostatnim okresie"
            >
              ⚠ decay
            </span>
          )}
          {cannib && (
            <span
              className="text-[9px] px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-600 dark:text-orange-400 font-medium border border-orange-500/20"
              title="Kanibalizacja — inny URL z tej domeny rankuje na tę samą frazę"
            >
              ⚠ kanibalizacja
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function ScopeBadge({
  clusterFit,
  method,
  reason,
}: {
  clusterFit: string | null;
  method: string | null;
  reason: string | null;
}) {
  if (!clusterFit) {
    return (
      <Badge variant="outline" className="text-[10px] text-muted-foreground">
        —
      </Badge>
    );
  }
  const cfg: Record<string, { label: string; color: string }> = {
    in_scope: {
      label: "in scope",
      color: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
    },
    adjacent: {
      label: "adjacent",
      color: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
    },
    off_topic: {
      label: "off topic",
      color: "bg-red-500/15 text-red-700 dark:text-red-400 border-red-500/30",
    },
  };
  const c = cfg[clusterFit];
  if (!c) return <Badge variant="outline" className="text-[10px]">{clusterFit}</Badge>;
  const title = [reason, method === "manual_override" ? "(manual override)" : undefined]
    .filter(Boolean)
    .join(" ");
  return (
    <Badge className={`text-[10px] ${c.color} border`} title={title || undefined}>
      {c.label}
      {method === "manual_override" && <span className="ml-1 text-[9px]">✎</span>}
    </Badge>
  );
}

function SroBadge({
  extractedAt,
  queryFrame,
  entity,
}: {
  extractedAt: string | null;
  queryFrame: string | null;
  entity: string | null;
}) {
  if (!extractedAt) {
    return <Badge variant="outline" className="text-[10px] text-muted-foreground">bez SRO</Badge>;
  }
  const frame = queryFrame && QUERY_FRAME_LABELS[queryFrame];
  return (
    <div className="flex flex-col gap-0.5 min-w-0" title={`${entity || "?"} · ${queryFrame || "?"}`}>
      {frame ? (
        <Badge className={`text-[10px] ${frame.color} border`}>
          {frame.label}
        </Badge>
      ) : (
        <Badge variant="secondary" className="text-[10px]">{queryFrame || "—"}</Badge>
      )}
      {entity && (
        <span className="text-[10px] text-muted-foreground truncate">{entity}</span>
      )}
    </div>
  );
}

function AllFlags({ flags }: { flags: string[] }) {
  if (!flags || flags.length === 0) {
    return <span className="text-xs text-green-600/80">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1 max-w-full">
      {flags.map((f) => (
        <Badge
          key={f}
          variant="destructive"
          className="text-[10px] px-1.5 py-0 whitespace-nowrap"
        >
          {QUALITY_FLAGS_LABELS[f] || f}
        </Badge>
      ))}
    </div>
  );
}

function decodeHtmlEntities(s: string): string {
  // Minimal dekoder encji — dla excerpt/meta HTML
  return s
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, " ")
    .replace(/<[^>]+>/g, "") // strip HTML tags
    .replace(/\s+/g, " ")
    .trim();
}

function buildPreview(row: { excerpt: string | null; meta_description: string | null }): string {
  const src = row.meta_description || row.excerpt || "";
  if (!src) return "";
  return decodeHtmlEntities(src).slice(0, 180);
}

function formatRel(ts: string | null): string {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    return formatDistanceToNow(d, { addSuffix: true, locale: pl });
  } catch {
    return ts;
  }
}

function SortHeader({
  label,
  active,
  dir,
  onClick,
  align,
}: {
  label: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
  align?: "right";
}) {
  const Icon = !active ? ArrowUpDown : dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1 hover:text-foreground transition-colors ${
        active ? "text-foreground font-semibold" : "text-muted-foreground"
      } ${align === "right" ? "ml-auto" : ""}`}
    >
      {label}
      <Icon className="h-3 w-3" />
    </button>
  );
}

export function WpPostsTable({ posts, loading }: WpPostsTableProps) {
  // Rozwinieta domyslnie — user chce od razu widziec liste
  const [open, setOpen] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [recFilter, setRecFilter] = useState<string>("all");
  const [sroFilter, setSroFilter] = useState<string>("all");
  const [seoFilter, setSeoFilter] = useState<string>("all");
  const [scopeFilter, setScopeFilter] = useState<string>("all");
  const [sortKey, setSortKey] = useState<SortKey>("published_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [previewId, setPreviewId] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const filtered = useMemo(() => {
    let rows = posts;
    if (recFilter !== "all") {
      rows = rows.filter((r) => (r.quality_recommendation || "") === recFilter);
    }
    if (sroFilter === "with") {
      rows = rows.filter((r) => !!r.sro_extracted_at);
    } else if (sroFilter === "without") {
      rows = rows.filter((r) => !r.sro_extracted_at);
    }
    if (seoFilter === "ranking") {
      rows = rows.filter((r) => (r.seo_total_keywords ?? 0) > 0);
    } else if (seoFilter === "top10") {
      rows = rows.filter((r) => (r.seo_top_10_count ?? 0) >= 1);
    } else if (seoFilter === "top3") {
      rows = rows.filter((r) => (r.seo_top_3_count ?? 0) >= 1);
    } else if (seoFilter === "decay") {
      rows = rows.filter((r) => !!r.seo_content_decay);
    } else if (seoFilter === "cannib") {
      rows = rows.filter((r) => !!r.seo_has_cannibalization);
    } else if (seoFilter === "no_ranking") {
      rows = rows.filter((r) => !!r.seo_last_sync && (r.seo_total_keywords ?? 0) === 0);
    }
    if (scopeFilter === "in_scope") {
      rows = rows.filter((r) => r.cluster_fit === "in_scope");
    } else if (scopeFilter === "adjacent") {
      rows = rows.filter((r) => r.cluster_fit === "adjacent");
    } else if (scopeFilter === "off_topic") {
      rows = rows.filter((r) => r.cluster_fit === "off_topic");
    } else if (scopeFilter === "not_classified") {
      rows = rows.filter((r) => !r.cluster_fit);
    }
    if (debouncedSearch.trim()) {
      const q = debouncedSearch.toLowerCase();
      rows = rows.filter((r) => r.title.toLowerCase().includes(q));
    }
    return [...rows].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === "string" && typeof bv === "string") {
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      return sortDir === "asc" ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });
  }, [posts, debouncedSearch, recFilter, sroFilter, seoFilter, scopeFilter, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const empty = posts.length === 0;

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="border rounded-lg mt-4">
      <CollapsibleTrigger
        disabled={empty}
        className={`w-full flex items-center justify-between px-3 py-2 hover:bg-muted/40 transition-colors ${
          empty ? "opacity-50 cursor-not-allowed" : ""
        }`}
      >
        <div className="flex items-center gap-2 text-sm font-medium">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Lista pobranych wpisów ({posts.length})
        </div>
        {loading && <span className="text-xs text-muted-foreground">Ładowanie...</span>}
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="border-t p-3 space-y-3">
          {/* Toolbar */}
          <div className="flex items-center gap-3 flex-wrap">
            <Input
              placeholder="Szukaj po tytule..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-xs h-8"
            />
            <Select value={recFilter} onValueChange={setRecFilter}>
              <SelectTrigger className="w-44 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Wszystkie rekomendacje</SelectItem>
                <SelectItem value="keep">Keep</SelectItem>
                <SelectItem value="borderline">Borderline</SelectItem>
                <SelectItem value="refurbish">Refurbish</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sroFilter} onValueChange={setSroFilter}>
              <SelectTrigger className="w-40 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">SRO: wszystkie</SelectItem>
                <SelectItem value="with">Z SRO</SelectItem>
                <SelectItem value="without">Bez SRO</SelectItem>
              </SelectContent>
            </Select>
            <Select value={seoFilter} onValueChange={setSeoFilter}>
              <SelectTrigger className="w-44 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">SEO: wszystkie</SelectItem>
                <SelectItem value="ranking">Z rankingami</SelectItem>
                <SelectItem value="top10">Top 10 (≥1)</SelectItem>
                <SelectItem value="top3">Top 3 (≥1)</SelectItem>
                <SelectItem value="decay">⚠ Content decay</SelectItem>
                <SelectItem value="cannib">⚠ Kanibalizacja</SelectItem>
                <SelectItem value="no_ranking">Brak rankingów</SelectItem>
              </SelectContent>
            </Select>
            <Select value={scopeFilter} onValueChange={setScopeFilter}>
              <SelectTrigger className="w-44 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Scope: wszystkie</SelectItem>
                <SelectItem value="in_scope">in_scope</SelectItem>
                <SelectItem value="adjacent">adjacent</SelectItem>
                <SelectItem value="off_topic">off_topic</SelectItem>
                <SelectItem value="not_classified">Nie sklasyfikowane</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground ml-auto">
              Pokazuję {filtered.length} z {posts.length}
            </span>
          </div>

          {/* Table — separatory kolumn (border-r), szersze kolumny, wieksze paddingi */}
          <div className="rounded-lg border bg-background max-h-[520px] overflow-y-auto">
            <table className="w-full text-sm table-fixed border-collapse">
              <colgroup>
                <col className="w-[18%]" />{/* Tytul + excerpt */}
                <col className="w-[60px]" />{/* Slow */}
                <col className="w-[62px]" />{/* H2/H3 */}
                <col className="w-[80px]" />{/* Jakosc */}
                <col className="w-[115px]" />{/* Rekomendacja */}
                <col className="w-[115px]" />{/* SRO */}
                <col className="w-[105px]" />{/* Scope */}
                <col className="w-[180px]" />{/* SEO */}
                <col />{/* Problemy */}
                <col className="w-[104px]" />{/* Opublikowano */}
                <col className="w-[104px]" />{/* Zmodyfikowano */}
                <col className="w-[44px]" />{/* Podglad */}
              </colgroup>
              <thead className="bg-muted/60 sticky top-0 z-10 border-b-2 border-border">
                <tr className="[&>th:not(:last-child)]:border-r [&>th]:border-border/60 [&>th]:font-semibold [&>th]:text-[11px] [&>th]:uppercase [&>th]:tracking-wider [&>th]:text-muted-foreground">
                  <th className="text-left px-3 py-3 whitespace-nowrap">Tytuł</th>
                  <th className="text-right px-3 py-3 whitespace-nowrap">
                    <SortHeader
                      label="Słów"
                      active={sortKey === "word_count"}
                      dir={sortDir}
                      onClick={() => toggleSort("word_count")}
                      align="right"
                    />
                  </th>
                  <th className="text-center px-3 py-3 whitespace-nowrap">H2/H3</th>
                  <th className="text-left px-3 py-3 whitespace-nowrap">
                    <SortHeader
                      label="Jakość"
                      active={sortKey === "quality_score"}
                      dir={sortDir}
                      onClick={() => toggleSort("quality_score")}
                    />
                  </th>
                  <th className="text-left px-3 py-3 whitespace-nowrap">Rekomendacja</th>
                  <th
                    className="text-left px-3 py-3 whitespace-nowrap"
                    title="Semantic Relevance Optimization — entity + attribute + query frame wyciągnięte przez LLM z pierwszych 100 słów"
                  >
                    SRO
                  </th>
                  <th
                    className="text-left px-3 py-3 whitespace-nowrap"
                    title="Cluster Fit: klasyfikacja względem scope mapy (in_scope / adjacent / off_topic). LLM lub auto_matched jeśli zmatchowany."
                  >
                    Scope
                  </th>
                  <th
                    className="text-left px-3 py-3 whitespace-nowrap"
                    title="Dane SEO z Senuto: liczba keywordów, top3/top10, estymacja traffic/mies, content decay, kanibalizacja"
                  >
                    SEO
                  </th>
                  <th className="text-left px-3 py-3 whitespace-nowrap">Problemy</th>
                  <th className="text-left px-3 py-3 whitespace-nowrap">
                    <SortHeader
                      label="Opublikowano"
                      active={sortKey === "published_at"}
                      dir={sortDir}
                      onClick={() => toggleSort("published_at")}
                    />
                  </th>
                  <th className="text-left px-3 py-3 whitespace-nowrap">Zmodyfikowano</th>
                  <th className="px-3 py-3 text-center" aria-label="Podgląd"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const sameModified =
                    p.modified_at && p.published_at && p.modified_at === p.published_at;
                  const preview = buildPreview(p);
                  return (
                    <tr
                      key={p.id}
                      className="border-b border-border/40 hover:bg-muted/40 align-top cursor-pointer transition-colors [&>td:not(:last-child)]:border-r [&>td]:border-border/30"
                      onClick={() => setPreviewId(p.id)}
                    >
                      <td className="px-3 py-3">
                        <div className="flex flex-col gap-1 min-w-0">
                          {p.wp_url ? (
                            <a
                              href={p.wp_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-primary hover:underline inline-flex items-center gap-1 font-medium leading-snug"
                            >
                              <span className="truncate">{p.title}</span>
                              <ExternalLink className="h-3 w-3 shrink-0" />
                            </a>
                          ) : (
                            <span className="font-medium truncate">{p.title}</span>
                          )}
                          {preview && (
                            <span
                              className="text-xs text-muted-foreground line-clamp-2"
                              title={preview}
                            >
                              {preview}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums">{p.word_count ?? "—"}</td>
                      <td className="px-3 py-3 text-center text-muted-foreground tabular-nums">
                        {p.h2_count ?? 0} / {p.h3_count ?? 0}
                      </td>
                      <td className="px-3 py-3">
                        <QualityBadge
                          score={p.quality_score}
                          recommendation={p.quality_recommendation}
                        />
                      </td>
                      <td className="px-3 py-3">
                        <RecommendationBadge rec={p.quality_recommendation} />
                      </td>
                      <td className="px-3 py-3">
                        <SroBadge
                          extractedAt={p.sro_extracted_at}
                          queryFrame={p.extracted_query_frame}
                          entity={p.extracted_entity}
                        />
                      </td>
                      <td className="px-3 py-3">
                        <ScopeBadge
                          clusterFit={p.cluster_fit}
                          method={p.cluster_fit_method}
                          reason={p.cluster_fit_reason}
                        />
                      </td>
                      <td className="px-3 py-3">
                        <SeoBadge
                          lastSync={p.seo_last_sync}
                          totalKeywords={p.seo_total_keywords}
                          top3={p.seo_top_3_count}
                          top10={p.seo_top_10_count}
                          top50={p.seo_top_50_count}
                          traffic={p.seo_organic_traffic}
                          decay={p.seo_content_decay}
                          cannib={p.seo_has_cannibalization}
                        />
                      </td>
                      <td className="px-3 py-3">
                        <AllFlags flags={p.quality_red_flags} />
                      </td>
                      <td className="px-3 py-3 text-xs text-muted-foreground whitespace-nowrap">
                        {formatRel(p.published_at)}
                      </td>
                      <td
                        className={`px-3 py-3 text-xs whitespace-nowrap ${
                          sameModified ? "text-muted-foreground/50" : "text-muted-foreground"
                        }`}
                      >
                        {sameModified ? "—" : formatRel(p.modified_at)}
                      </td>
                      <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => setPreviewId(p.id)}
                          title="Podgląd wpisu"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={12} className="p-6 text-center text-sm text-muted-foreground">
                      Brak wpisów spełniających kryteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </CollapsibleContent>

      <WpPostPreviewModal postId={previewId} onClose={() => setPreviewId(null)} />
    </Collapsible>
  );
}
